// v13
// Path: frontend/src/components/SyncHub/shared/PresetAccordionItem.jsx
// Inputs: preset, isExpanded, onToggleExpand, token, sourceSystem, targetSystem, availableTables, targetEntities, assignedEntitiesInSet, onSavePreset, onDuplicatePreset, onRenamePreset, onDeletePreset, onResetPresetDefaults, onSavingStateChange
// Outputs: Interactive 5-column Tree Grid pipeline row with inline ghost actions, strict z-[45] stacking, and nested Field Mapping Designer table
// Summary: High-performance pipeline accordion row featuring 5-column grid alignment, Column 5 inline ghost actions, text-sm typographic scaling, sticky elevation scroll shadow, and debounced auto-saving.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchableSelect from '../SearchableSelect';
import MappingDesignerTable from './MappingDesignerTable';
import { getQbEntityMeta, normalizeEntityName } from '../entityCatalogs';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields with existing preset mappings
// Condensed rule: DO NOT pre-render all unmapped fields from target schema!
// ONLY retain: 1. Auto-spawned required fields, 2. Actively mapped rows, 3. User-created placeholder rows.
const mergeTargetFieldsWithMappingsCondensed = (tFields, rawMappings) => {
    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                label: m.label || m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || '',
                isCustom: !!m.isCustom
            });
        }
    });

    // Auto-spawn required destination fields from target schema if missing
    (tFields || []).forEach(tf => {
        const isRequired = !!tf.required || (typeof tf.rules === 'string' && tf.rules.toUpperCase().includes('REQUIRED'));
        const lower = tf.name.toLowerCase();
        if (isRequired && !existingMap.has(lower)) {
            existingMap.set(lower, {
                target: tf.name,
                label: tf.label || tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: '',
                isRequired: true
            });
        }
    });

    return Array.from(existingMap.values());
};

export default function PresetAccordionItem({
    preset,
    isExpanded = false,
    onToggleExpand = () => {},
    token,
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    availableTables = [],
    targetEntities = [],
    assignedEntitiesInSet = new Set(),
    onSavePreset = async () => {},
    onDuplicatePreset = () => {},
    onRenamePreset = () => {},
    onDeletePreset = () => {},
    onResetPresetDefaults = () => {},
    onSavingStateChange = () => {}
}) {
    const containerRef = useRef(null);
    const headerRowRef = useRef(null);

    // Current entity and table for this preset pipeline
    const canonicalEntity = normalizeEntityName(preset.TargetQBEntity || preset.EntityType || 'Customer');
    const entityMeta = getQbEntityMeta(canonicalEntity);
    const defaultTable = preset.SourceTable || entityMeta?.defaultTable || '';

    const [selectedTable, setSelectedTable] = useState(defaultTable);
    const [selectedEntity, setSelectedEntity] = useState(canonicalEntity);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [loadingDefinitions, setLoadingDefinitions] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Inline pipeline name editing state (Double-click feature)
    const [isEditingName, setIsEditingName] = useState(false);
    const [pipelineName, setPipelineName] = useState(preset.MappingName || `${canonicalEntity} Pipeline`);

    // Safe delete UX tracking
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(false);

    // Tier 2 HUD field search & filter toggles
    const [fieldSearch, setFieldSearch] = useState('');
    const [mappingFilter, setMappingFilter] = useState('all'); // 'all' | 'mapped' | 'unmapped'

    // Debounced Auto-Save Refs
    const saveTimerRef = useRef(null);
    const latestStateRef = useRef({ mappings, selectedTable, selectedEntity });

    useEffect(() => {
        latestStateRef.current = { mappings, selectedTable, selectedEntity };
    }, [mappings, selectedTable, selectedEntity]);

    // Sync state if preset updates from parent
    useEffect(() => {
        setSelectedTable(preset.SourceTable || entityMeta?.defaultTable || '');
        setSelectedEntity(canonicalEntity);
        setPipelineName(preset.MappingName || `${canonicalEntity} Pipeline`);
    }, [preset.SourceTable, preset.MappingName, canonicalEntity, entityMeta?.defaultTable]);


    // Fetch field definitions when expanded
    const fetchDefinitions = useCallback(async () => {
        if (!isExpanded) return;
        setLoadingDefinitions(true);
        try {
            const activeTbl = selectedTable || preset.SourceTable || '';
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceDatabaseName: 'pbsdata00',
                tableName: activeTbl
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                setDefinitions(data);

                let initialRaw = Array.isArray(preset.mappings) ? preset.mappings : [];
                if ((!initialRaw || initialRaw.length === 0) && preset.FieldMappingsJson) {
                    try { initialRaw = JSON.parse(preset.FieldMappingsJson); } catch(e) {}
                }

                const targetFields = data.targetFields || [];
                const merged = mergeTargetFieldsWithMappingsCondensed(targetFields, initialRaw);
                setMappings(merged);
                setIsDirty(false);
            }
        } catch (err) {
            console.error('[PresetAccordionItem] Error loading definitions:', err);
        } finally {
            setLoadingDefinitions(false);
        }
    }, [isExpanded, selectedTable, selectedEntity, preset.SourceTable, token]);

    useEffect(() => {
        if (isExpanded) {
            fetchDefinitions();
        }
    }, [isExpanded, fetchDefinitions]);

    // Optimistic Save Routine
    const executeSave = useCallback(async (stateToSave) => {
        const { mappings: curMappings, selectedTable: curTable, selectedEntity: curEntity } = stateToSave;
        setSaving(true);
        onSavingStateChange(true);
        try {
            const cleanMappings = curMappings
                .filter(m => m.target)
                .map(m => ({
                    target: m.target.trim(),
                    label: m.label ? m.label.trim() : undefined,
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : (m.source ? [m.source.trim()] : []),
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            await onSavePreset({
                mappingId: preset.MappingID,
                setName: preset.SetName,
                sourceSystem: preset.SourceSystem || sourceSystem,
                targetSystem: preset.TargetSystem || targetSystem,
                entityType: curEntity,
                targetQBEntity: curEntity,
                sourceTable: curTable,
                mappingName: preset.MappingName,
                mappings: cleanMappings
            });

            setIsDirty(false);
        } catch (err) {
            console.error('[PresetAccordionItem] Auto-save error:', err);
        } finally {
            setSaving(false);
            onSavingStateChange(false);
        }
    }, [preset, sourceSystem, targetSystem, onSavePreset, onSavingStateChange]);

    // Trigger debounced save
    const triggerDebouncedSave = useCallback(() => {
        setIsDirty(true);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            executeSave(latestStateRef.current);
        }, 1500);
    }, [executeSave]);

    // Flush pending save immediately on collapse
    useEffect(() => {
        if (!isExpanded && isDirty) {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            executeSave(latestStateRef.current);
        }
    }, [isExpanded, isDirty, executeSave]);

    // Compute live mapping coverage count
    const mappedFieldsCount = useMemo(() => {
        if (mappings.length > 0) {
            return mappings.filter(m => {
                const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return s.length > 0 && s.some(x => x && x.trim().length > 0);
            }).length;
        }
        const raw = Array.isArray(preset.mappings) ? preset.mappings : [];
        return raw.filter(m => {
            const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            return s.length > 0 && s.some(x => x && x.trim().length > 0);
        }).length;
    }, [mappings, preset.mappings]);

    // Mutate field mapping row
    const handleCustomMappingChange = (targetName, field, val) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const updated = { ...m, [field]: val };
                if (field === 'sources') {
                    const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                    updated.source = Array.isArray(val) ? val.join(delim) : String(val);
                }
                return updated;
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Remove mapping row (immediate save so caller can await)
    const handleRemoveCustomRow = async (targetName) => {
        const nextMappings = mappings.filter(m => (m.target || '').toLowerCase() !== targetName.toLowerCase());
        setMappings(nextMappings);
        await executeSave({ mappings: nextMappings, selectedTable, selectedEntity });
    };

    // Reset mapping row (clear assigned sources)
    const handleResetMappingRow = (targetName) => {
        setMappings(prev => prev.map(m => {
            if ((m.target || '').toLowerCase() === targetName.toLowerCase()) {
                return { ...m, sources: [], source: '' };
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Add new mapping from Tier 3 Ghost Row
    const handleAddMappingFromGhost = (newMapping) => {
        if (!newMapping.target) return;
        setMappings(prev => {
            const exists = prev.some(m => (m.target || '').toLowerCase() === newMapping.target.toLowerCase());
            if (exists) return prev;
            return [...prev, newMapping];
        });
        triggerDebouncedSave();
    };

    // Prepare table dropdown options
    const tableOptions = useMemo(() => {
        return availableTables.map(t => {
            const tName = typeof t === 'string' ? t : (t.name || t.TableName || '');
            const fCount = t.fieldCount || t.FieldCount || (t.columns ? t.columns.length : 0);
            const countStr = fCount > 0 ? ` (${fCount} cols)` : '';
            return {
                value: tName,
                label: `${tName}${countStr}`,
                description: t.description || t.Description || undefined,
                isPowered: tName.toUpperCase() === (selectedTable || '').toUpperCase() && mappedFieldsCount > 0
            };
        });
    }, [availableTables, selectedTable, mappedFieldsCount]);

    // Prepare entity dropdown options strictly excluding other pipelines' entities in this Set
    const entityOptions = useMemo(() => {
        const curLower = selectedEntity.toLowerCase();
        return targetEntities
            .filter(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                return eName.toLowerCase() === curLower || !assignedEntitiesInSet.has(eName.toLowerCase());
            })
            .map(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                const eLabel = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
                const isCurrent = eName.toLowerCase() === curLower;
                return {
                    value: eName,
                    label: eLabel,
                    isPowered: isCurrent ? mappedFieldsCount > 0 : false
                };
            });
    }, [targetEntities, selectedEntity, assignedEntitiesInSet, mappedFieldsCount]);

    return (
        <div
            ref={containerRef}
            className={`transition-colors duration-200 border-b ${
                isExpanded
                    ? 'bg-slate-800 border-l-4 border-indigo-500 border-b-slate-750 shadow-lg'
                    : 'border-slate-700/50 bg-slate-900/60 hover:bg-slate-850/60'
            }`}
        >
            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 2: 5-COLUMN TREE GRID PIPELINE HEADER ROW                     */}
            {/* Ultra-condensed h-9, px-2 py-0.5, sticky top-[92px]                */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div
                ref={headerRowRef}
                className={`grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)_70px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 select-none transition-colors border-b relative group scroll-mt-[92px] ${
                    deleteError ? 'border-rose-500 bg-rose-950/20' : 'border-slate-700/50'
                } ${
                    isDeleting
                        ? 'opacity-50 text-slate-500 bg-slate-900'
                        : isExpanded
                            ? 'sticky top-[92px] z-[45] bg-slate-800 shadow-md shadow-black/40 text-white'
                            : 'bg-slate-900 hover:bg-slate-850 text-slate-300'
                }`}
            >
                {/* ── COL 1 (250px): EXPAND CHEVRON BUTTON + PIPELINE TITLE ── */}
                <div
                    onClick={!isEditingName ? onToggleExpand : undefined}
                    className={`flex items-center gap-1.5 min-w-0 h-full select-none ${
                        !isEditingName ? 'cursor-pointer' : ''
                    }`}
                    title={!isEditingName ? (isExpanded ? "Collapse pipeline" : "Expand pipeline") : undefined}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand();
                        }}
                        className="p-1 hover:bg-slate-700/60 rounded text-slate-400 hover:text-indigo-300 transition-colors flex items-center justify-center shrink-0 focus:outline-none"
                        title={isExpanded ? "Collapse pipeline" : "Expand pipeline"}
                    >
                        <span className={`text-xs transition-transform duration-200 inline-block ${isExpanded ? 'rotate-90 text-indigo-400' : ''}`}>
                            ▶
                        </span>
                    </button>
                    {isEditingName ? (
                        <input
                            type="text"
                            autoFocus
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onBlur={() => {
                                setIsEditingName(false);
                                if (pipelineName.trim() && pipelineName.trim() !== preset.MappingName) {
                                    onRenamePreset({ ...preset, MappingName: pipelineName.trim() });
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    setIsEditingName(false);
                                    if (pipelineName.trim() && pipelineName.trim() !== preset.MappingName) {
                                        onRenamePreset({ ...preset, MappingName: pipelineName.trim() });
                                    }
                                } else if (e.key === 'Escape') {
                                    setPipelineName(preset.MappingName || `${canonicalEntity} Pipeline`);
                                    setIsEditingName(false);
                                }
                            }}
                            className="bg-slate-950 border border-indigo-500 rounded px-1.5 py-0 h-6 text-sm text-white focus:outline-none w-full"
                        />
                    ) : (
                        <span
                            className="font-bold text-sm text-white tracking-wide truncate hover:text-indigo-300 transition-colors"
                        >
                            {pipelineName}
                        </span>
                    )}
                </div>

                {/* ── COL 2 (1fr): ERP SOURCE TABLE (SEARCHABLE SELECT) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={tableOptions}
                        value={selectedTable}
                        onChange={tbl => {
                            const upper = (tbl || '').toUpperCase();
                            setSelectedTable(upper);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select source table..."
                        allowCustom={true}
                        className="w-full text-sm"
                    />
                </div>

                {/* ── COL 3 (80px, center): DIRECTIONAL FLOW ARROW ── */}
                <div className="flex items-center justify-center gap-1 text-center select-none">
                    <span className="text-slate-500 font-bold text-xs">➔</span>
                    <span className="px-1.5 py-0.2 rounded-full bg-slate-900 border border-slate-750 text-indigo-300 font-mono text-[10px]">
                        {mappedFieldsCount} Mapped
                    </span>
                </div>

                {/* ── COL 4 (1fr): TARGET TABLE (FILTERED) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={entityOptions}
                        value={selectedEntity}
                        onChange={ent => {
                            const canonical = normalizeEntityName(ent);
                            setSelectedEntity(canonical);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select target table..."
                        className="w-full text-sm"
                    />
                </div>

                {/* ── COL 5 (70px): INLINE GHOST ACTIONS (RENAME, RESET, DELETE) ── */}
                <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex gap-1.5 items-center justify-end pr-1">
                    {/* Rename Button (Pencil) */}
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditingName(true);
                        }}
                        className="h-6 w-6 rounded hover:bg-slate-700/60 text-slate-400 hover:text-white flex items-center justify-center transition-colors focus:outline-none"
                        title="Rename pipeline"
                    >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                    </button>

                    {/* Reset Button (Undo) */}
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={(e) => {
                            e.stopPropagation();
                            onResetPresetDefaults(preset);
                        }}
                        className="h-6 w-6 rounded hover:bg-slate-700/60 text-slate-400 hover:text-white flex items-center justify-center transition-colors disabled:opacity-40 focus:outline-none"
                        title="Reset pipeline mappings to defaults"
                    >
                        <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                    </button>

                    {/* Delete Button (Trash) */}
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={async (e) => {
                            e.stopPropagation();
                            setIsDeleting(true);
                            setDeleteError(false);
                            try {
                                const res = await onDeletePreset(preset);
                                if (res === false) {
                                    setIsDeleting(false);
                                }
                            } catch (err) {
                                setDeleteError(true);
                                setIsDeleting(false);
                            }
                        }}
                        className="h-6 w-6 rounded hover:bg-rose-950/60 text-slate-400 hover:text-rose-400 flex items-center justify-center transition-colors focus:outline-none"
                        title="Delete pipeline"
                    >
                        {isDeleting ? (
                            <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                        ) : (
                            <span className="material-symbols-outlined text-[14px]">delete</span>
                        )}
                    </button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 3: CSS GRID SLIDE ANIMATION CONTAINER                         */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className={`min-h-0 ${isExpanded ? 'overflow-visible' : 'overflow-hidden'}`}>
                    {loadingDefinitions ? (
                        <div className="py-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                            <span className="material-symbols-outlined text-indigo-400 animate-spin text-sm">sync</span>
                            Loading field schema definitions...
                        </div>
                    ) : (
                        <div className="p-0">
                            <MappingDesignerTable
                                isStandardMode={true}
                                selectedQBEntity={selectedEntity}
                                activeTableName={selectedTable}
                                mappings={mappings}
                                definitions={definitions}
                                sourceColumns={definitions?.sourceFields || definitions?.sourceColumns || []}
                                onAddMapping={handleAddMappingFromGhost}
                                handleCustomMappingChange={handleCustomMappingChange}
                                handleRemoveCustomRow={handleRemoveCustomRow}
                                handleResetMappingRow={handleResetMappingRow}
                                fieldSearch={fieldSearch}
                                setFieldSearch={setFieldSearch}
                                mappingFilter={mappingFilter}
                                setMappingFilter={setMappingFilter}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
